import { useFlags } from '@atlaskit/flag';
import { IAddUserRequestDto } from 'core/api/users/users-api-interface';
import UsersApiService from 'core/api/users/users-api.service';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag } from 'core/utilities/flags-helper';
import { useCallback, useMemo, useState } from 'react';
import { IButton } from 'shared/components/buttons/button-interface';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import SharedForm from 'shared/components/form/form';
import AddUserInfoDialog from '../add-user-info-dialog/add-user-info-dialog';
import { IAddUserFormOutput } from './add-user-dialog-interface';
import { IUserDao } from 'core/api/types/users.interface';
import { ISharedFormField } from 'shared/components/form/form-interface';
import { ControlType } from 'core/enums/control-type';
import { InputType } from 'core/enums/input-type';
import { getRoleFromKey, Roles } from 'core/config/roles';

interface IAddUserDialog {
  user?: IUserDao;
}

const AddUserDialog = ({ user }: IAddUserDialog) => {
  const [formSubmitting, setFormSubmitting] = useState(false);
  const isCreating = !user;

  const getDefaultValue = useCallback(
    (key: string) => {
      if (!user) return undefined;
      switch (key) {
        case 'role':
          let roles = user.roles;
          if (!user.roles) {
            roles = [user.role!];
          }
          return roles?.map((roleKey) => {
            const role = getRoleFromKey(roleKey);
            return {
              value: role?.key ?? '',
              label: role?.label ?? 'Unknown',
            };
          });
        case 'fullName':
          return user.fullName;
        case 'emailAddress':
          return user.emailAddress;
        default:
          return undefined;
      }
    },
    [user],
  );

  const fields: ISharedFormField[] = useMemo(
    () =>
      [
        {
          key: 'fullName',
          control: ControlType.TextField,
          type: InputType.Text,
          label: 'Full Name',
          required: true,
        },
        {
          key: 'emailAddress',
          control: ControlType.TextField,
          type: InputType.Email,
          label: 'Email address',
          required: true,
        },
        {
          key: 'role',
          control: ControlType.SelectMulti,
          label: 'User Role',
          required: true,
          options: Roles.map((role) => ({ value: role.key, label: role.label })),
        },
        {
          key: 'resetPassword',
          control: ControlType.Switch,
          label: 'Trigger Password Reset',
          required: false,
          defaultValue: false,
          hidden: isCreating,
        },
      ].map((field) => ({
        ...field,
        defaultValue: getDefaultValue(field.key),
      })),
    [getDefaultValue, isCreating],
  );

  const flags = useFlags();
  const dialog = useDialog();

  const addUser = async (data: IAddUserFormOutput) => {
    setFormSubmitting(true);
    const payload: IAddUserRequestDto = {
      emailAddress: data.emailAddress,
      roles: data.role.map((role) => role.value),
      fullName: data.fullName,
      ...(user?.role && { role: user.role }),
    };
    if (isCreating) {
      try {
        const response = await UsersApiService.add(payload);
        dialog?.replaceDialog(
          <AddUserInfoDialog fullName={data.fullName} emailAddress={data.emailAddress} temporaryPassword={response} />,
        );
      } catch (error) {
        showErrorFlag(
          'User creation failed',
          `Unable to create an account for ${data.fullName}, please try again.`,
          flags,
        );
        setFormSubmitting(false);
      }
    } else {
      try {
        await UsersApiService.update(user.uid, payload);
        dialog?.closeDialog();
      } catch (error) {
        showErrorFlag('User update failed', `Unable to update the user, please try again.`, flags);
        setFormSubmitting(false);
      }
    }
  };

  const cancelButton: IButton = {
    onClick: () => dialog?.closeDialog(),
    label: 'Cancel',
    appearance: 'subtle',
    type: 'button',
  };

  const customContent = () => {
    return (
      <SharedForm
        className='px-4 pb-4 pt-2 overflow-y-auto'
        onSubmit={addUser}
        fields={fields}
        buttonLabel='Submit'
        loading={formSubmitting}
        cancelButton={cancelButton}
      />
    );
  };

  return <SharedDialogBase title='Add user' customContentTemplate={customContent()} showButtons={false} />;
};

export default AddUserDialog;
